/**
 * Ring-Buffer Logger for Birch
 * Captures recent application events, warnings, and errors in a fixed-size circular buffer.
 * Automatically scrubs potential PII (tokens, passwords, emails, long text blocks).
 */

export interface LogEntry {
  timestamp: string;
  level: "log" | "info" | "warn" | "error" | "breadcrumb";
  message: string;
  context?: Record<string, unknown>;
}

const MAX_ENTRIES = 80;
const ringBuffer: LogEntry[] = [];

// Regex patterns to scrub sensitive data
const TOKEN_PATTERN = /(Bearer\s+[A-Za-z0-9-_=.]+)|(sb-[a-zA-Z0-9-_=]+)|(key-[a-zA-Z0-9-_=]+)/gi;
const EMAIL_PATTERN = /([a-zA-Z0-9_.+-]+)@([a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+)/gi;
const PASSWORD_PATTERN = /(password|secret|apikey|token)["']?\s*[:=]\s*["']?([^"',\s]+)/gi;

/**
 * Sanitize strings to remove tokens, credentials, and excessive manuscript content
 */
export function sanitizeLogString(str: string): string {
  if (!str || typeof str !== "string") return "";
  
  let cleaned = str
    .replace(TOKEN_PATTERN, "[REDACTED_TOKEN]")
    .replace(PASSWORD_PATTERN, "$1: [REDACTED]")
    .replace(EMAIL_PATTERN, (_match, user, domain) => {
      const maskedUser = user.length > 2 ? `${user.slice(0, 2)}***` : "***";
      return `${maskedUser}@${domain}`;
    });

  // Limit single log message size to avoid capturing large pasted manuscript paragraphs
  if (cleaned.length > 500) {
    cleaned = cleaned.slice(0, 500) + " ...[TRUNCATED]";
  }

  return cleaned;
}

function addEntry(level: LogEntry["level"], message: string, context?: Record<string, unknown>) {
  const sanitizedMsg = sanitizeLogString(message);
  
  let sanitizedContext: Record<string, unknown> | undefined;
  if (context && typeof context === "object") {
    try {
      sanitizedContext = JSON.parse(sanitizeLogString(JSON.stringify(context)));
    } catch {
      sanitizedContext = undefined;
    }
  }

  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    message: sanitizedMsg,
    context: sanitizedContext,
  };

  ringBuffer.push(entry);
  if (ringBuffer.length > MAX_ENTRIES) {
    ringBuffer.shift();
  }
}

/**
 * Global error listeners to capture uncaught errors into the ring buffer
 */
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    const errorMsg = event.error?.message || event.message || "Unknown window error";
    const stack = event.error?.stack ? `\nStack: ${event.error.stack.slice(0, 300)}` : "";
    addEntry("error", `[UncaughtError] ${errorMsg}${stack}`);
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason instanceof Error
      ? `${event.reason.message}\n${event.reason.stack || ""}`
      : String(event.reason);
    addEntry("error", `[UnhandledRejection] ${reason}`);
  });
}

export const ringLogger = {
  log: (msg: string, ctx?: Record<string, unknown>) => addEntry("log", msg, ctx),
  info: (msg: string, ctx?: Record<string, unknown>) => addEntry("info", msg, ctx),
  warn: (msg: string, ctx?: Record<string, unknown>) => addEntry("warn", msg, ctx),
  error: (msg: string, ctx?: Record<string, unknown>) => addEntry("error", msg, ctx),
  breadcrumb: (category: string, action: string, data?: Record<string, unknown>) => {
    addEntry("breadcrumb", `[${category}] ${action}`, data);
  },

  /**
   * Return a snapshot of current logs formatted as plain text
   */
  getFormattedLogs(): string {
    if (ringBuffer.length === 0) return "(No logs recorded)";
    return ringBuffer
      .map((e) => {
        const ctx = e.context ? ` | ctx: ${JSON.stringify(e.context)}` : "";
        return `[${e.timestamp}] [${e.level.toUpperCase()}] ${e.message}${ctx}`;
      })
      .join("\n");
  },

  /**
   * Return structured log array
   */
  getEntries(): LogEntry[] {
    return [...ringBuffer];
  },

  /**
   * Clear buffer (primarily for testing)
   */
  clear() {
    ringBuffer.length = 0;
  }
};
